import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, Archive, Folder, X, HelpCircle } from "lucide-react";
import { httpClient } from "../../../HttpClient";
import { dialog } from "../../../components/ui/Dialog";
import { useEtatGlobal } from "../../EtatGlobal.js";
import ChargeParCollaborateurSection from "./TraitementsComptables/organisms/ChargeParCollaborateurSection";
import TraitementsComptablesTable from "./TraitementsComptables/organisms/TraitementsComptablesTable";
import ClotureDetailsOverlay from "./TraitementsComptables/organisms/ClotureDetailsOverlay";
import ClotureStageModal from "./TraitementsComptables/organisms/ClotureStageModal";
import NotifierPopover from "./TraitementsComptables/organisms/NotifierPopover";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import OverlaySuiviCompta from "./Compta/OverlaySuiviCompta";
import TutorialOverlay from "../../../components/ui/TutorialOverlay";
import traitementsComptablesTutorial from "../../Tutoriel/TraitementsComptablesTutorial";
import { buildMonthTasks, estBloque } from "./suiviProdUtils";
import "./TraitementsComptables/TraitementsComptables.css";

// Filtres par niveau, repris de la maquette : le statut "À affecter" vit dans
// le select collaborateur (valeur __none__), pas dans le select statut.
const EMPTY_STAGE_FILTER = { status: "all", collab: "" };

// Rôle "de terrain" : ne voit que ses dossiers affectés (niveau 1, 2 ou 3) —
// même restriction qu'en backend (getAllTickets), reprise ici pour que l'UI
// reflète la même règle sans attendre un refetch. SECRETARIAT en est
// volontairement exclu (demande Jonathan, 18/09/2026) : voir le commentaire
// équivalent dans traitementsComptablesController.js.
const ROLES_VISIBILITE_RESTREINTE = ["COLLABORATEUR"];

// perimetreIds : identifiants de l'utilisateur et de ses subordonnés dans la
// hiérarchie (même règle qu'en backend) — il voit aussi les dossiers de son
// équipe.
function estVisiblePourUtilisateur(ticket, utilisateur, perimetreIds) {
  if (
    !utilisateur ||
    utilisateur.allCabinetsAccess ||
    !ROLES_VISIBILITE_RESTREINTE.includes(utilisateur.role)
  ) {
    return true;
  }
  return ticket.etapes.some(
    (e) =>
      String(e.assignee?.id) === String(utilisateur.id) ||
      (perimetreIds && perimetreIds.has(String(e.assignee?.id))),
  );
}

function initiales(user) {
  return `${user.name?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
}

function SuiviTraitementsComptables() {
  const {
    utilisateur,
    users,
    traitementsComptablesData: tickets,
    setTraitementsComptablesData: setTickets,
    loadingTraitementsComptables: loading,
    fetchTraitementsComptables: fetchTickets,
    suiviComptaData,
    fetchSuiviCompta,
    todos: blocsHierarchie,
  } = useEtatGlobal();
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilters, setStageFilters] = useState({
    1: { ...EMPTY_STAGE_FILTER },
    2: { ...EMPTY_STAGE_FILTER },
    3: { ...EMPTY_STAGE_FILTER },
  });
  // Tri par défaut : arrivée la plus récente, comme dans la maquette.
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [showArchives, setShowArchives] = useState(false);

  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [ficheClientData, setFicheClientData] = useState(null);
  const [ficheClientLoading, setFicheClientLoading] = useState(false);
  const ficheClientCache = useRef({});

  const [clotureTarget, setClotureTarget] = useState(null);
  const [notifierTarget, setNotifierTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  // Bouclage Suivi Compta ouvert après confirmation, une fois l'étape
  // Révision clôturée (voir ClotureStageModal.onBouclerCompta) — liaison
  // Traitements comptables <-> Suivi Compta.
  const [bouclageOverlay, setBouclageOverlay] = useState(null);
  const [bouclageSelectedTask, setBouclageSelectedTask] = useState(null);

  // Rafraîchissement automatique toutes les 15s pour refléter les affectations
  // / clôtures faites par d'autres collaborateurs. Volontairement indépendant
  // de fetchTraitementsComptables (EtatGlobal.js) : son garde-fou anti-
  // skeleton ("si la liste est déjà chargée") repose sur une closure figée au
  // moment où cet effet capture la fonction — comme l'effet ne tourne qu'une
  // fois (dépendances vides, pour ne pas relancer l'intervalle à chaque
  // rendu), cette closure reste bloquée sur l'état "liste vide" du tout
  // premier rendu et redéclenchait donc le skeleton à chaque tick. Appel
  // direct à l'API + mise à jour silencieuse de la liste, sans jamais
  // toucher à l'état de chargement.
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await httpClient.get(
          "https://backend-myalfa.vercel.app/api/traitements-comptables/tickets",
        );
        setTickets(data.data || []);
      } catch (err) {
        console.error(
          "Erreur lors du rafraîchissement automatique des traitements comptables :",
          err,
        );
      }
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ouvrirBouclageCompta = (ticket) => {
    const client = suiviComptaData.find((c) => c.id === ticket.client?.id);
    if (!client) {
      dialog.alert(
        "Ce client n'a pas encore de suivi Compta initialisé — ouvrez Suivi Compta pour l'initialiser avant de boucler.",
      );
      return;
    }
    const dateArrivee = new Date(ticket.dateArrivee);
    const month = dateArrivee.getMonth() + 1;
    const year = dateArrivee.getFullYear();
    setBouclageOverlay({ client, month, year });
    setBouclageSelectedTask(
      buildMonthTasks(client, month, year).find((t) => t.type === "Bouclage mensuel") || null,
    );
  };

  // Menu "Collaborateur" flottant en position fixed, rendu une seule fois ici
  // (hors du tableau) pour échapper au overflow:auto de .pieces-wrap — même
  // principe que le menu "..." de ToDo.js (handleMenuToggle).
  const [collabMenu, setCollabMenu] = useState(null);

  const handleOpenCollabMenu = (rect, ticket, niveau, assignee) => {
    if (
      collabMenu &&
      collabMenu.ticket.id === ticket.id &&
      collabMenu.niveau === niveau
    ) {
      setCollabMenu(null);
      return;
    }
    if (!rect) return;
    const menuHeight = 330;
    const top =
      rect.bottom + menuHeight > window.innerHeight
        ? rect.top - menuHeight
        : rect.bottom + 4;
    setCollabMenu({ top, left: rect.left, ticket, niveau, assignee });
  };

  useEffect(() => {
    if (!collabMenu) return;
    const handleClickOutside = () => setCollabMenu(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [collabMenu]);

  // Visibilité restreinte pour le rôle de terrain (COLLABORATEUR) : ne voit
  // que les dossiers où il est affecté niveau 1, 2 ou 3 (voir
  // estVisiblePourUtilisateur). SECRETARIAT voit tous les dossiers.
  const perimetreIds = useMemo(
    () => new Set((blocsHierarchie || []).map((b) => String(b.userId))),
    [blocsHierarchie],
  );
  const visibleTickets = useMemo(
    () => tickets.filter((t) => estVisiblePourUtilisateur(t, utilisateur, perimetreIds)),
    [tickets, utilisateur, perimetreIds],
  );

  // Un dossier est archivé quand ses 3 étapes sont clôturées ; le bouton
  // Archives bascule le tableau entre dossiers actifs et archivés.
  const isArchived = (ticket) =>
    ticket.etapes.every((e) => e.statut === "CLOTURE");
  const activeTickets = useMemo(
    () => visibleTickets.filter((t) => !isArchived(t)),
    [visibleTickets],
  );
  const archivedCount = visibleTickets.length - activeTickets.length;
  const scopedTickets = useMemo(
    () => (showArchives ? visibleTickets.filter(isArchived) : activeTickets),
    [visibleTickets, activeTickets, showArchives],
  );

  const matchStatus = (etape, key) => {
    if (key === "all") return true;
    if (key === "enCours")
      return !!etape.assignee && etape.statut !== "CLOTURE";
    if (key === "enRetard") return !!etape.enRetardLive;
    if (key === "cloture") return etape.statut === "CLOTURE";
    return true;
  };

  const matchCollab = (etape, value) => {
    if (!value) return true;
    if (value === "__none__") return !etape.assignee;
    return String(etape.assignee?.id) === String(value);
  };

  // Pipeline : périmètre (actifs / archives) → recherche → filtres par niveau → tri
  const filteredTickets = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const dir = sort.dir === "asc" ? 1 : -1;
    return scopedTickets
      .filter((ticket) => {
        if (term && !ticket.client?.nom?.toLowerCase().includes(term))
          return false;
        return ticket.etapes.every((etape) => {
          const filtre = stageFilters[etape.niveau];
          if (!filtre) return true;
          return (
            matchStatus(etape, filtre.status) &&
            matchCollab(etape, filtre.collab)
          );
        });
      })
      .sort((a, b) => {
        if (sort.key === "client") {
          return (
            (a.client?.nom || "").localeCompare(b.client?.nom || "", "fr") * dir
          );
        }
        return String(a.dateArrivee).localeCompare(String(b.dateArrivee)) * dir;
      });
  }, [scopedTickets, searchTerm, stageFilters, sort]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "client" ? "asc" : "desc" },
    );

  const anyFilterActive =
    !!searchTerm.trim() ||
    [1, 2, 3].some(
      (n) => stageFilters[n].status !== "all" || stageFilters[n].collab,
    );

  const resetFilters = () => {
    setSearchTerm("");
    setStageFilters({
      1: { ...EMPTY_STAGE_FILTER },
      2: { ...EMPTY_STAGE_FILTER },
      3: { ...EMPTY_STAGE_FILTER },
    });
  };

  // Charge par collaborateur : dossiers en retard d'abord dans chaque colonne,
  // colonnes triées par charge décroissante (comme dans la maquette).
  // Seules les étapes réellement "EN_COURS" comptent — une étape affectée par
  // défaut (niveau 2/3 hérité du portefeuille) mais encore VERROUILLE tant
  // que l'étape précédente n'est pas clôturée ne doit pas apparaître ici.
  const chargeParCollaborateur = useMemo(() => {
    const map = new Map();
    activeTickets.forEach((ticket) => {
      // Dossier bloqué (litige) : grisé dans le tableau, hors charge collaborateur.
      if (estBloque(ticket.client)) return;
      ticket.etapes.forEach((etape) => {
        if (etape.statut !== "EN_COURS") return;
        const key = etape.assignee.id;
        if (!map.has(key)) {
          map.set(key, { user: etape.assignee, dossiers: [] });
        }
        map.get(key).dossiers.push({
          clientNom: ticket.client?.nom,
          overdue: etape.enRetardLive,
        });
      });
    });
    const colonnes = Array.from(map.values());
    colonnes.forEach((col) =>
      col.dossiers.sort((a, b) =>
        a.overdue === b.overdue ? 0 : a.overdue ? -1 : 1,
      ),
    );
    colonnes.sort((a, b) => b.dossiers.length - a.dossiers.length);
    return colonnes;
  }, [activeTickets]);

  const aAffecter = useMemo(
    () =>
      activeTickets.reduce(
        (total, ticket) =>
          estBloque(ticket.client)
            ? total
            : total +
              ticket.etapes.filter((e) => !e.assignee && e.statut !== "CLOTURE")
                .length,
        0,
      ),
    [activeTickets],
  );

  const handleStageFilterChange = (niveau, field, value) => {
    setStageFilters((prev) => ({
      ...prev,
      [niveau]: { ...prev[niveau], [field]: value },
    }));
  };

  // Patch local d'une étape (mise à jour optimiste) : l'UI change
  // immédiatement, la requête part en arrière-plan, et en cas d'erreur on
  // restaure les valeurs précédentes via ce même helper.
  const patchEtapeLocale = (ticketId, niveau, patch) => {
    setTickets((prev) =>
      prev.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              etapes: ticket.etapes.map((etape) =>
                etape.niveau === niveau ? { ...etape, ...patch } : etape,
              ),
            }
          : ticket,
      ),
    );
  };

  const handleAssign = async (ticket, niveau, userId) => {
    const etape = ticket.etapes.find((e) => e.niveau === niveau);
    if (!etape) return;

    const assignee = userId
      ? users.find((u) => String(u.id) === String(userId))
      : null;
    const rollback = { assignee: etape.assignee, statut: etape.statut };
    // Même dérivation de statut que le backend : affecté = EN_COURS, sinon
    // A_AFFECTER (l'étape est forcément non close et déverrouillée ici).
    patchEtapeLocale(ticket.id, niveau, {
      assignee: assignee
        ? {
            id: assignee.id,
            name: assignee.name,
            lastName: assignee.lastName,
            poste: assignee.poste,
          }
        : null,
      statut: assignee ? "EN_COURS" : "A_AFFECTER",
    });

    try {
      await httpClient.patch(
        `https://backend-myalfa.vercel.app/api/traitements-comptables/tickets/${ticket.id}/etapes/${niveau}/affectation`,
        { assigneeId: userId },
      );

      // Mails au nouvel affecté et à l'ancien : envoyés par le serveur avec
      // l'affectation (backend updateAffectation, 28/10/2026).
    } catch (err) {
      console.error("Erreur affectation :", err);
      patchEtapeLocale(ticket.id, niveau, rollback);
      dialog.alert(
        "L'affectation n'a pas pu être enregistrée. Veuillez réessayer.",
        {
          title: "Erreur d'affectation",
        },
      );
    }
  };

  const handleEcheanceChange = async (ticketId, niveau, dueDate) => {
    const ticket = tickets.find((t) => t.id === ticketId);
    const etape = ticket?.etapes.find((e) => e.niveau === niveau);
    if (!etape) return;

    const rollback = {
      dueDate: etape.dueDate,
      enRetardLive: etape.enRetardLive,
    };
    patchEtapeLocale(ticketId, niveau, {
      dueDate: dueDate || null,
      enRetardLive:
        !etape.closedAt && !!dueDate && new Date(dueDate) < new Date(),
    });

    try {
      await httpClient.patch(
        `https://backend-myalfa.vercel.app/api/traitements-comptables/tickets/${ticketId}/etapes/${niveau}/affectation`,
        { dueDate },
      );
    } catch (err) {
      console.error("Erreur échéance :", err);
      patchEtapeLocale(ticketId, niveau, rollback);
      dialog.alert(
        "L'échéance n'a pas pu être enregistrée. Veuillez réessayer.",
        {
          title: "Erreur d'échéance",
        },
      );
    }
  };

  const handleNotify = (ticket, etape) => setNotifierTarget({ ticket, etape });

  const handleCloture = (ticket, etape) => setClotureTarget({ ticket, etape });

  const handleShowDetails = (ticket, etape) => setDetailsTarget({ ticket, etape });

  const showClientDetails = async (client) => {
    if (!client?.id) return;
    setFicheClientOpen(true);
    if (ficheClientCache.current[client.id]) {
      setFicheClientData(ficheClientCache.current[client.id]);
      setFicheClientLoading(false);
      return;
    }
    setFicheClientData(client);
    setFicheClientLoading(true);
    try {
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/clients/${client.id}`,
      );
      ficheClientCache.current[client.id] = data;
      setFicheClientData(data);
    } catch (err) {
      console.error("Erreur chargement fiche client :", err);
    } finally {
      setFicheClientLoading(false);
    }
  };

  const nbAffiches = filteredTickets.length;

  return (
    <div className="tc-scope">
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        {/* Titre */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900 m-0">
              Traitements comptables
            </h1>
            <span className="text-xs font-semibold text-[#9B1B5D] bg-[#9B1B5D]/10 px-2 py-0.5 rounded-full">
              Nouveau
            </span>
            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              title="Voir le tutoriel"
              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
            >
              <HelpCircle size={14} />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Pièces reçues des clients — suivi par niveau d'intervention
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchives((v) => !v)}
            className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
              showArchives
                ? "bg-[#6e1a36] text-white"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            {showArchives ? <Folder size={15} /> : <Archive size={15} />}
            {showArchives ? "Dossiers actifs" : "Archives"}
            {archivedCount > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  showArchives
                    ? "bg-white/25 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {archivedCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {!showArchives && (
        <ChargeParCollaborateurSection
          charge={chargeParCollaborateur}
          aAffecter={aAffecter}
        />
      )}

      <div className="flex items-center gap-3.5 flex-wrap mb-2.5">
        <div className="relative flex-none w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Filtrer par client…"
            className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
          />
        </div>
        {anyFilterActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
          >
            <X size={14} /> Retirer tous les filtres
          </button>
        )}
        <div className="text-xs text-gray-400">
          {nbAffiches} dossier{nbAffiches > 1 ? "s" : ""} affiché
          {nbAffiches > 1 ? "s" : ""}
          {nbAffiches !== scopedTickets.length && (
            <span> sur {scopedTickets.length}</span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mt-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full" style={{ fontSize: "0.72rem" }}>
            <thead>
              <tr className="bg-[#6e1a36]">
                {[
                  "Date d'arrivée",
                  "Client",
                  "Nature des pièces",
                  "Période",
                  "Niveau 1",
                  "Niveau 2",
                  "Niveau 3",
                ].map((col, i) => (
                  <th key={i} className="px-3 py-3.5 text-left">
                    <div
                      className="h-3 rounded bg-[#ffffff33] animate-pulse"
                      style={{ width: "70%" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {["45%", "18%", "22%", "18%", "30%", "30%", "30%"].map(
                    (w, j) => (
                      <td key={j} className="px-3 py-3">
                        <div
                          className="h-3 rounded bg-slate-200 animate-pulse"
                          style={{ width: w }}
                        />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <TraitementsComptablesTable
          tickets={filteredTickets}
          users={users}
          stageFilters={stageFilters}
          onStageFilterChange={handleStageFilterChange}
          onOpenCollabMenu={handleOpenCollabMenu}
          onEcheanceChange={handleEcheanceChange}
          onNotify={handleNotify}
          onCloture={handleCloture}
          onShowDetails={handleShowDetails}
          onClientClick={showClientDetails}
          sort={sort}
          onToggleSort={toggleSort}
          showArchives={showArchives}
        />
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={(updated) => {
          ficheClientCache.current[updated.id] = updated;
          setFicheClientData(updated);
        }}
      />

      {clotureTarget && (
        <ClotureStageModal
          ticket={clotureTarget.ticket}
          etape={clotureTarget.etape}
          users={users}
          utilisateur={utilisateur}
          onClose={() => setClotureTarget(null)}
          onSubmitted={fetchTickets}
          onBouclerCompta={() => ouvrirBouclageCompta(clotureTarget.ticket)}
        />
      )}

      {bouclageOverlay && (
        <OverlaySuiviCompta
          selectedClient={bouclageOverlay.client}
          selectedMonth={bouclageOverlay.month}
          selectedYear={bouclageOverlay.year}
          selectedTask={bouclageSelectedTask}
          setSelectedTask={setBouclageSelectedTask}
          setShowOverlay={(v) => {
            if (!v) {
              setBouclageOverlay(null);
              setBouclageSelectedTask(null);
            }
          }}
          getMonthTasks={() =>
            buildMonthTasks(bouclageOverlay.client, bouclageOverlay.month, bouclageOverlay.year)
          }
          refetch={fetchSuiviCompta}
        />
      )}

      {notifierTarget && (
        <NotifierPopover
          ticket={notifierTarget.ticket}
          etape={notifierTarget.etape}
          users={users}
          utilisateur={utilisateur}
          onClose={() => setNotifierTarget(null)}
        />
      )}

      {detailsTarget && (
        <ClotureDetailsOverlay
          ticket={detailsTarget.ticket}
          etape={detailsTarget.etape}
          onClose={() => setDetailsTarget(null)}
        />
      )}

      {showTutorial && (
        <TutorialOverlay
          steps={traitementsComptablesTutorial}
          onClose={() => setShowTutorial(false)}
        />
      )}

      {/* Menu "Collaborateur" flottant en position fixed pour échapper à
          l'overflow:auto du tableau (même principe que ToDo.js) */}
      {collabMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: collabMenu.top,
            left: collabMenu.left,
            zIndex: 9999,
          }}
          className="ir-collab-menu"
        >
          <button
            type="button"
            className={`ir-collab-opt ${!collabMenu.assignee ? "is-active" : ""}`}
            onClick={() => {
              handleAssign(collabMenu.ticket, collabMenu.niveau, null);
              setCollabMenu(null);
            }}
          >
            <span className="ir-collab-avatar ir-collab-avatar-empty">
              <X size={10} />
            </span>
            <span>Non affecté</span>
          </button>
          <div className="ir-collab-sep"></div>
          {users.map((u) => (
            <button
              type="button"
              key={u.id}
              className={`ir-collab-opt ${
                String(collabMenu.assignee?.id) === String(u.id)
                  ? "is-active"
                  : ""
              }`}
              onClick={() => {
                handleAssign(collabMenu.ticket, collabMenu.niveau, u.id);
                setCollabMenu(null);
              }}
            >
              <span className="ir-collab-avatar">{initiales(u)}</span>
              <span>
                {u.name} {u.lastName || ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuiviTraitementsComptables;
